import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { API, formatApiError } from "@/lib/api";
import { Upload, Download, Loader2, CheckCircle2, AlertTriangle, PackageSearch, History } from "lucide-react";

export default function Products() {
  const [products, setProducts] = useState([]);
  const [logs, setLogs] = useState([]);
  const [result, setResult] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => {
    api.get("/admin/products").then(({ data }) => setProducts(data));
    api.get("/admin/import-logs").then(({ data }) => setLogs(data));
  };
  useEffect(() => { load(); }, []);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/products/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      toast.success(`Import complete — ${data.created} created, ${data.updated} updated`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Import failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div>
      <h1 className="font-serif-display text-3xl font-bold text-slate-50">Products & Bulk Import</h1>
      <p className="mt-1 text-sm text-[#94A3B8]">Upload a CSV to add or update catalogue items and their Sage SKU stock mappings in one go.</p>

      <div className="mt-8 grid gap-5 lg:grid-cols-3">
        <div className="rounded-2xl border border-dashed border-[#D4AF37]/40 bg-[#1A1D24] p-6 lg:col-span-2">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#D4AF37]/10 text-[#D4AF37]"><Upload className="h-5 w-5" /></div>
            <div>
              <div className="font-semibold text-slate-100">Bulk CSV Import</div>
              <div className="text-xs text-[#94A3B8]">Existing SKUs are updated; new SKUs are created. Stock & tier pricing included.</div>
            </div>
          </div>
          <div className="mt-5 flex flex-wrap items-center gap-3">
            <input ref={fileRef} data-testid="product-import-input" type="file" accept=".csv" onChange={onFile} className="hidden" id="csv-upload" />
            <label htmlFor="csv-upload" data-testid="product-import-btn"
              className="mc-btn-gold inline-flex cursor-pointer items-center gap-2 rounded-full px-6 py-3 text-sm">
              {uploading ? <><Loader2 className="h-4 w-4 animate-spin" /> Importing…</> : <><Upload className="h-4 w-4" /> Choose CSV File</>}
            </label>
            <a data-testid="product-template-btn" href={`${API}/admin/products/import-template`}
              className="inline-flex items-center gap-2 rounded-full border border-[#2A2E39] px-6 py-3 text-sm font-semibold text-slate-200 hover:border-[#D4AF37]/50 hover:text-[#D4AF37]">
              <Download className="h-4 w-4" /> Download Template
            </a>
          </div>

          {result && (
            <div data-testid="import-result" className="mt-5 rounded-xl border border-[#2A2E39] bg-[#0B0C0E] p-4">
              <div className="flex items-center gap-4 text-sm">
                <span className="inline-flex items-center gap-1.5 text-emerald-400"><CheckCircle2 className="h-4 w-4" /> {result.created} created</span>
                <span className="inline-flex items-center gap-1.5 text-[#D4AF37]"><CheckCircle2 className="h-4 w-4" /> {result.updated} updated</span>
                {result.total_errors > 0 && <span className="inline-flex items-center gap-1.5 text-rose-400"><AlertTriangle className="h-4 w-4" /> {result.total_errors} errors</span>}
              </div>
              {result.errors?.length > 0 && (
                <ul className="mt-3 space-y-1 text-xs text-rose-300">
                  {result.errors.slice(0, 8).map((e, i) => <li key={`${e.row}-${i}`}>Row {e.row}: {e.error}</li>)}
                </ul>
              )}
            </div>
          )}
        </div>

        <div className="rounded-2xl border border-[#2A2E39] bg-[#1A1D24] p-6">
          <div className="flex items-center gap-2 text-slate-100"><PackageSearch className="h-5 w-5 text-[#D4AF37]" /> <span className="font-semibold">Catalogue</span></div>
          <div className="mt-3 text-4xl font-bold text-[#D4AF37]">{products.length}</div>
          <div className="text-xs text-[#94A3B8]">active products</div>
        </div>
      </div>

      {logs.length > 0 && (
        <div className="mt-8">
          <div className="flex items-center gap-2 text-slate-100"><History className="h-5 w-5 text-[#D4AF37]" /> <span className="font-semibold">Import History</span></div>
          <div className="mt-3 overflow-x-auto rounded-2xl border border-[#2A2E39]">
            <table className="w-full text-left text-sm">
              <thead className="bg-[#121418] text-xs uppercase tracking-wider text-[#64748B]">
                <tr>{["When", "File", "By", "Created", "Updated", "Errors"].map((h) => <th key={h} className="whitespace-nowrap px-4 py-3">{h}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-[#2A2E39]">
                {logs.map((l) => (
                  <tr key={l.id} data-testid={`import-log-${l.id}`} className="mc-table-row">
                    <td className="whitespace-nowrap px-4 py-3 text-xs text-[#94A3B8]">{new Date(l.run_at).toLocaleString("en-IE")}</td>
                    <td className="px-4 py-3 font-mono-data text-slate-100">{l.filename}</td>
                    <td className="px-4 py-3 text-[#94A3B8]">{l.admin_email}</td>
                    <td className="px-4 py-3 font-mono-data text-emerald-400">{l.created}</td>
                    <td className="px-4 py-3 font-mono-data text-[#D4AF37]">{l.updated}</td>
                    <td className="px-4 py-3 font-mono-data text-rose-400">{l.errors}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="mt-8 overflow-x-auto rounded-2xl border border-[#2A2E39]">
        <table className="w-full text-left text-sm">
          <thead className="bg-[#121418] text-xs uppercase tracking-wider text-[#64748B]">
            <tr>{["SKU", "Product", "Brand", "Category", "Trade Price", "Tier A", "Tier B", "Sage Stock", "Available"].map((h) => <th key={h} className="whitespace-nowrap px-4 py-3">{h}</th>)}</tr>          </thead>
          <tbody className="divide-y divide-[#2A2E39]">
            {products.map((p) => (
              <tr key={p.sku} data-testid={`product-row-${p.sku}`} className="mc-table-row">
                <td className="whitespace-nowrap px-4 py-3 font-mono-data text-[#D4AF37]">{p.sku}</td>
                <td className="px-4 py-3 text-slate-100">{p.name}</td>
                <td className="px-4 py-3 text-[#94A3B8]">{p.brand}</td>
                <td className="px-4 py-3 text-[#94A3B8]">{p.category}</td>
                <td className="px-4 py-3 font-mono-data text-slate-100">€{p.trade_price?.toFixed(2)}</td>
                <td className="px-4 py-3 font-mono-data text-[#94A3B8]">{p.price_tiers?.tier_a ? `€${p.price_tiers.tier_a.toFixed(2)}` : "—"}</td>
                <td className="px-4 py-3 font-mono-data text-[#94A3B8]">{p.price_tiers?.tier_b ? `€${p.price_tiers.tier_b.toFixed(2)}` : "—"}</td>
                <td className="px-4 py-3 font-mono-data text-slate-100">{p.sage_stock}</td>
                <td className="px-4 py-3 font-mono-data font-bold text-slate-50">{p.available}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
